import config from '../../config';
import { navigateToExternal, isEmpty } from '../../core/util';
import { convertUberToBpId } from '../../flows/internal-account-finder/loaders';

export const accountPathTypes = {
  NEW_ACCOUNT: 'NEW ACCOUNT',
  EXISTING_ACCOUNT: 'EXISTING ACCOUNT'
};

export const productTypes = {
  RETIREMENT: 'Retirement',
  INVESTING: 'General Investing',
  COLLEGE_SAVINGS: 'College Savings',
  TRANSFER: 'Transfer',
  ROLLOVER: 'Rollover'
};

export const accountOptionTypes = {
  ROTH_IRA: 'Roth IRA',
  TRADITIONAL_IRA: 'Traditional IRA',
  ROLLOVER_IRA_EXTERNAL: 'Rollover IRA (External to TRP)',
  ROLLOVER_IRA_INTERNAL: 'Traditional Rollover IRA (Internal from RPS - OCR)',
  ROLLOVER_IRA_INTERNAL_TRAC:
    'Traditional Rollover IRA (Internal from RPS TRAC - OCR)',
  TRANSFER_SEP_IRA: 'Transfer SEP-IRA',
  TRANSFER_TRADITIONAL: 'Transfer Traditional IRA',
  TRANSFER_ROTH: 'Transfer Roth IRA',
  TRANSFER_ROLLOVER: 'Transfer Rollover IRA',
  INDIVIDUAL: 'Individual',
  JOINT: 'Joint',
  UGMA: 'UGMA',
  ROTH_ROLLOVER_IRA_INTERNAL:
    'Roth Rollover IRA (Internal from RPS - Roth OCR)',
  ROTH_ROLLOVER_IRA_INTERNAL_TRAC:
    'Roth Rollover IRA (Internal from RPS TRAC - Roth OCR)'
};

export const accountOptionText = {
  ROLLOVER_IRA_EXTERNAL:
    'Client wants to roll over assets from an old external employer-sponsored plan into a Traditional IRA.',
  ROLLOVER_IRA_INTERNAL:
    'One Call Rollover - Client wants to roll over assets from an OMNI RPS plan into a Traditional Rollover IRA.',
  ROLLOVER_IRA_INTERNAL_TRAC:
    'One Call Rollover - Client wants to roll over assets from a TRAC RPS plan into a Traditional Rollover IRA.',
  TRANSFER_SEP_IRA:
    'Move a SEP-IRA from another institution into a Traditional IRA.',
  TRANSFER_TRADITIONAL: 'Move an existing IRA from another institution.',
  TRANSFER_ROTH: 'Move an existing Roth IRA from another institution.',
  TRANSFER_ROLLOVER: 'Move an existing Rollover IRA from another institution.',
  ROTH_ROLLOVER_IRA_INTERNAL:
    'One Call Roth Rollover - Client wants to roll over assets from an OMNI RPS plan into a Roth Rollover IRA.',
  ROTH_ROLLOVER_IRA_INTERNAL_TRAC:
    'One Call Roth Rollover - Client wants to roll over assets from a TRAC RPS plan into a Roth Rollover IRA.'
};

export const newAccountProductTypeOptions = ['Retirement', 'General Investing'];

export const existingAccountProductTypeOptions = ['Rollover', 'Transfer'];

export const newRetirementOptions = ['Roth IRA', 'Traditional IRA'];

export const existingTransferOptions = [
  'Transfer Roth IRA',
  'Transfer Traditional IRA',
  'Transfer Rollover IRA',
  'Transfer SEP-IRA'
];

export const existingRolloverOptions = [
  'Rollover IRA (External to TRP)',
  'Traditional Rollover IRA (Internal from RPS - OCR)',
  'Traditional Rollover IRA (Internal from RPS TRAC - OCR)',
  'Roth Rollover IRA (Internal from RPS - Roth OCR)',
  'Roth Rollover IRA (Internal from RPS TRAC - Roth OCR)'
];

export const generalInvestingOptions = ['Individual', 'Joint', 'UGMA '];

const foNASUPrefixUrl = 'personal-investing/tools/nasu/front-office';
const foNASUMFPrefixUrl = `${foNASUPrefixUrl}/mutual-fund`;
export const foNASURPSOMNIPrefixUrl = `${foNASUPrefixUrl}/rps-omni`;
export const foNASURPSTRACPrefixUrl = `${foNASUPrefixUrl}/rps-trac`;
const foNASUMFTransferPrefixUrl = `${foNASUMFPrefixUrl}/transfer`;
const foNASUMFContributoryPrefixUrl = `${foNASUMFPrefixUrl}/contributory`;
const foNASUMFTaxablePrefixUrl = `${foNASUMFPrefixUrl}/taxable`;
export const foNASUROTHOMNIPrefixUrl = `${foNASUPrefixUrl}/roth-rollover-omni`;
export const foNASUROTHTRACPrefixUrl = `${foNASUPrefixUrl}/roth-rollover-trac`;

export const displayProductTypeOptions = type => {
  switch (type) {
    case accountPathTypes.NEW_ACCOUNT:
      return newAccountProductTypeOptions;
    case accountPathTypes.EXISTING_ACCOUNT:
      return existingAccountProductTypeOptions;
    default:
      return [];
  }
};

const displayNewAccountTypeOptions = subType => {
  switch (subType) {
    case productTypes.RETIREMENT:
      return newRetirementOptions;
    case productTypes.INVESTING:
      return generalInvestingOptions;
    default:
      return [];
  }
};

const displayExistingAccountTypeOptions = subType => {
  switch (subType) {
    case productTypes.TRANSFER:
      return existingTransferOptions;
    default:
      return existingRolloverOptions;
  }
};

export const displayAccountTypeOptions = (type, subType) => {
  if (type === accountPathTypes.NEW_ACCOUNT) {
    const newAccountTypeOptions = displayNewAccountTypeOptions(subType);
    return newAccountTypeOptions;
  }

  const existingAccountTypeOptions = displayExistingAccountTypeOptions(subType);
  return existingAccountTypeOptions;
};

const getNewRetirementUrl = (accountType, bpId) => {
  switch (accountType) {
    case accountOptionTypes.ROTH_IRA:
      return `${foNASUMFContributoryPrefixUrl}/roth-ira?bpId=${bpId}`;
    case accountOptionTypes.TRADITIONAL_IRA:
      return `${foNASUMFContributoryPrefixUrl}/traditional-ira?bpId=${bpId}`;
    default:
      return '';
  }
};

const getTransferUrl = (accountType, bpId) => {
  switch (accountType) {
    case accountOptionTypes.TRANSFER_SEP_IRA:
      return `${foNASUMFTransferPrefixUrl}/sep-ira?bpId=${bpId}`;
    case accountOptionTypes.TRANSFER_ROTH:
      return `${foNASUMFTransferPrefixUrl}/roth-ira?bpId=${bpId}`;
    case accountOptionTypes.TRANSFER_ROLLOVER:
      return `${foNASUMFTransferPrefixUrl}/rollover-ira?bpId=${bpId}`;
    default:
      return `${foNASUMFTransferPrefixUrl}/traditional-ira?bpId=${bpId}`;
  }
};

const getRolloverUrl = (accountType, bpId) => {
  switch (accountType) {
    case accountOptionTypes.ROLLOVER_IRA_EXTERNAL:
      return `${foNASUMFPrefixUrl}/rollover?bpId=${bpId}`;
    case accountOptionTypes.ROLLOVER_IRA_INTERNAL:
      return `${foNASURPSOMNIPrefixUrl}?bpId=${bpId}`;
    case accountOptionTypes.ROLLOVER_IRA_INTERNAL_TRAC:
      return `${foNASURPSTRACPrefixUrl}?bpId=${bpId}`;
    case accountOptionTypes.ROTH_ROLLOVER_IRA_INTERNAL:
      return `${foNASUROTHOMNIPrefixUrl}?bpId=${bpId}`;
    default:
      return `${foNASUROTHTRACPrefixUrl}?bpId=${bpId}`;
  }
};

const getNewInvestingUrl = (accountType, bpId) => {
  switch (accountType) {
    case accountOptionTypes.INDIVIDUAL:
      return `${foNASUMFTaxablePrefixUrl}/individual?bpId=${bpId}`;
    case accountOptionTypes.JOINT:
      return `${foNASUMFTaxablePrefixUrl}/joint?bpId=${bpId}`;
    default:
      return `${foNASUMFTaxablePrefixUrl}/ugmautma?bpId=${bpId}`;
  }
};

/* const getNewBrokerageUrl = (accountType, bpId) => {
  const acctTypeParam = accountType
    ?.toLowerCase()
    .split(' ')
    .join('');

  switch (accountType) {
    case accountOptionTypes.TRADITIONAL_IRA:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=new&brkProductType=regular&acctType=tradira&src=AccountFinder&bpId=${bpId}`;
    case accountOptionTypes.ROLLOVER_IRA:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=rollover&src=AccountFinder&bpId=${bpId}`;
    case accountOptionTypes.INDIVIDUAL:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=new&brkProductType=regular&acctType=ind&src=AccountFinder&bpId=${bpId}`;
    case accountOptionTypes.JOINT:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=new&brkProductType=regular&acctType=jt&src=AccountFinder&bpId=${bpId}`;
    default:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=new&brkProductType=regular&acctType=${acctTypeParam}&src=AccountFinder&bpId=${bpId}`;
  }
};

const getExistingBrokerageUrl = (accountType, bpId) => {
  switch (accountType) {
    case accountOptionTypes.JOINT:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=transfer&brkProductType=regular&src=AccountFinder&resigningAcctType=Joint Tenant&bpId=${bpId}`;
    case accountOptionTypes.UGMA:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=transfer&brkProductType=regular&src=AccountFinder&resigningAcctType=Custodial&bpId=${bpId}`;
    default:
      return `nasubrk1/BrokerageNasu/secure/startNewAcctSetUp.do?path=transfer&brkProductType=regular&src=AccountFinder&resigningAcctType=${accountType}&bpId=${bpId}`;
  }
}; */

const getNewActUrl = (productType, accountType, bpId) => {
  let newActUrl = '';

  switch (productType) {
    case productTypes.RETIREMENT:
      newActUrl = getNewRetirementUrl(accountType, bpId);
      return newActUrl;
    case productTypes.INVESTING:
      newActUrl = getNewInvestingUrl(accountType, bpId);
      return newActUrl;
    default:
      return '';
  }
};

const getExistingActUrl = (productType, accountType, bpId) => {
  let existingActUrl = '';

  switch (productType) {
    case productTypes.TRANSFER:
      existingActUrl = getTransferUrl(accountType, bpId);
      return existingActUrl;
    case productTypes.ROLLOVER:
      existingActUrl = getRolloverUrl(accountType, bpId);
      return existingActUrl;
    // case productTypes.BROKERAGE:
    // existingActUrl = getExistingBrokerageUrl(accountType, bpId);
    // return existingActUrl;
    default:
      return '';
  }
};

const getTransferTypeText = accountType => {
  switch (accountType) {
    case accountOptionTypes.TRANSFER_SEP_IRA:
      return accountOptionText.TRANSFER_SEP_IRA;
    case accountOptionTypes.TRANSFER_TRADITIONAL:
      return accountOptionText.TRANSFER_TRADITIONAL;
    case accountOptionTypes.TRANSFER_ROTH:
      return accountOptionText.TRANSFER_ROTH;
    case accountOptionTypes.TRANSFER_ROLLOVER:
      return accountOptionText.TRANSFER_ROLLOVER;
    default:
      return '';
  }
};

const getRolloverTypeText = accountType => {
  switch (accountType) {
    case accountOptionTypes.ROLLOVER_IRA_EXTERNAL:
      return accountOptionText.ROLLOVER_IRA_EXTERNAL;
    case accountOptionTypes.ROLLOVER_IRA_INTERNAL:
      return accountOptionText.ROLLOVER_IRA_INTERNAL;
    case accountOptionTypes.ROLLOVER_IRA_INTERNAL_TRAC:
      return accountOptionText.ROLLOVER_IRA_INTERNAL_TRAC;
    default:
      return '';
  }
};

const getRothRolloverTypeText = accountType => {
  switch (accountType) {
    case accountOptionTypes.ROTH_ROLLOVER_IRA_INTERNAL:
      return accountOptionText.ROTH_ROLLOVER_IRA_INTERNAL;
    case accountOptionTypes.ROTH_ROLLOVER_IRA_INTERNAL_TRAC:
      return accountOptionText.ROTH_ROLLOVER_IRA_INTERNAL_TRAC;
    default:
      return '';
  }
};

export const displayAdditionalText = accountType => {
  let displayText = '';
  if (accountType.includes('Transfer')) {
    displayText = getTransferTypeText(accountType);
  } else if (accountType.includes('Roth')) {
    displayText = getRothRolloverTypeText(accountType);
  } else displayText = getRolloverTypeText(accountType);
  return displayText;
};

export const redirectToFONASU = async (
  accountPathType,
  productType,
  accountType,
  bpId
) => {
  let url = '';
  let bpIdValue = bpId;

  // DE67118 - convert uberId to bpId before navigating to FONASU form
  // salesforce will send uberId, but our services require the bpId
  if (isEmpty(bpIdValue)) {
    bpIdValue = await convertUberToBpId();
  }

  if (accountPathType === accountPathTypes.NEW_ACCOUNT) {
    url = getNewActUrl(productType, accountType, bpIdValue);
  } else {
    url = getExistingActUrl(productType, accountType, bpIdValue);
  }

  const ADFSUrlWithFederatedToken = config('internalADFSFederationURL');
  const fonasuUrlBase = config('internalReturnUrlPrefix');
  const fonasuUrl = `${ADFSUrlWithFederatedToken}${fonasuUrlBase}${url}`;

  return navigateToExternal(fonasuUrl);
};
